import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/it";
import {
  ArrowLeft,
  Calendar,
  CalendarClock,
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import TabsCst from "@components/TabsCst";
import EnrollmentListShell from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/EnrollmentListShell";
import { useEditionDetail } from "./hooks/useEditionDetail";
import {
  getEditionUserId,
  getEditionUserName,
  useEditionEnrolledUsers,
} from "./hooks/useEditionEnrolledUsers";
import { useEditionSessions } from "./hooks/useEditionSessions";
import SessionCard from "./components/SessionCard";
import RegistryTab from "./components/RegistryTab";
import SessionDetailModal, {
  DetailTab as SessionDetailTab,
} from "./components/SessionDetailModal";
import { openEditionEnrollmentModal } from "./EditionEnrollment/host";
import { openSessionWizardModal } from "./SessionWizard/host";
import { useSessionWizardSubmit } from "./SessionWizard/hooks/useSessionWizardSubmit";
import { useDeleteSession } from "./SessionWizard/hooks/useDeleteSession";
import {
  PermissionGate,
  PERMISSIONS,
  editionPerm,
  usePermissions,
} from "@utils/permissions";
import { openToast } from "@utils/toastStore";
import { EditionKind, EditionSession, EditionEnrolledUser } from "./types";
import { useDeleteEditionEnrollment } from "./hooks/useDeleteEditionEnrollment";
import { useDeleteEdition } from "./hooks/useDeleteEdition";
import { useEditionMailto } from "./hooks/useEditionMailto";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import {
  ServicesURL,
  formatSessionOccupiedError,
  getAllErrorsAsString,
  isSessionOverlapError,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useActivityDuration } from "./hooks/useActivityDuration";
import { hasSessionOverlap, totalMinutesWith } from "./sessionChecks";
import { SessionDraft } from "./SessionWizard/types";
import {
  EnrollableEdition,
  useRescheduleEnrollment,
} from "./hooks/useRescheduleEnrollment";
import RescheduleEnrollmentModal from "./components/RescheduleEnrollmentModal";
import RescheduleSessionModal from "./components/RescheduleSessionModal";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import { ButtonTertiaryIcon } from "src/UI/ButtonTertiaryIcon";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const ENROLLED_PAGE_SIZE = 10;

type DetailTab = "sessions" | "register" | "enrolled-users";

const formatRange = (start?: number, end?: number): string => {
  if (!start || !end) return "";
  const s = dayjs(start).locale("it");
  const e = dayjs(end).locale("it");
  return `Dal ${s.format("D MMMM YYYY")} al ${e.format("D MMMM YYYY")}`;
};

const EditionDetailPage: React.FC = () => {
  const { t } = useTranslation("edition-detail");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "", editionId = "" } = useParams();
  const [searchParams] = useSearchParams();

  const kind: EditionKind =
    searchParams.get("kind") === "virtual" ? "virtual" : "physical";

  const initialTab: DetailTab = ((): DetailTab => {
    const t = searchParams.get("tab");
    return t === "register" || t === "enrolled-users" ? t : "sessions";
  })();

  const {
    detail,
    isLoading,
    mutate: refetchDetail,
  } = useEditionDetail(corporateId, editionId, kind);
  const {
    sessions,
    isLoading: sessionsLoading,
    mutate: refetchSessions,
  } = useEditionSessions(corporateId, editionId, kind);

  const { submitSession, isSubmitting: isSessionSubmitting } =
    useSessionWizardSubmit(corporateId, editionId, kind);

  const { activityDuration } = useActivityDuration(
    corporateId,
    detail?.learning_objects?.id,
  );

  const [durationWarn, setDurationWarn] = useState<{
    resolve: (proceed: boolean) => void;
  } | null>(null);

  const submitSessionMapped = async (
    draft: SessionDraft,
    editSessionId?: string | number,
  ) => {
    try {
      await submitSession(draft, detail || {}, sessions, editSessionId);
    } catch (e: any) {
      throw new Error(
        isSessionOverlapError(e)
          ? formatSessionOccupiedError(e, t)
          : getAllErrorsAsString(e) || (e?.message ?? ""),
      );
    }
  };

  const confirmDurationIfNeeded = (
    draft: SessionDraft,
    editSessionId?: string | number,
  ): Promise<boolean> => {
    if (
      !activityDuration ||
      totalMinutesWith(draft, sessions, editSessionId) === activityDuration
    ) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => setDurationWarn({ resolve }));
  };

  const editionContextLabel = () =>
    detail?.learning_objects?.label || detail?.edition_name || undefined;

  const handleAddSession = () => {
    openSessionWizardModal({
      kind,
      corporateId,
      editionId,
      contextLabel: editionContextLabel(),
      publishDate: detail?.publish_date,
      onConfirm: async (draft) => {
        if (hasSessionOverlap(draft, sessions, undefined)) {
          throw new Error(String(t("session-already-exist-error")));
        }
        const proceed = await confirmDurationIfNeeded(draft, undefined);
        if (!proceed) {
          throw new Error(String(t("duration-warning.cancelled")));
        }
        await submitSessionMapped(draft, undefined);
        refetchSessions();
        refetchDetail();
      },
    });
  };

  const handleEditSession = (session: EditionSession) => {
    setDetailSession(null);
    openSessionWizardModal({
      kind,
      corporateId,
      editionId,
      contextLabel: editionContextLabel(),
      editSession: session,
      publishDate: detail?.publish_date,
      onConfirm: async (draft) => {
        if (hasSessionOverlap(draft, sessions, session.id)) {
          throw new Error(String(t("session-already-exist-error")));
        }
        const proceed = await confirmDurationIfNeeded(draft, session.id);
        if (!proceed) {
          throw new Error(String(t("duration-warning.cancelled")));
        }
        await submitSessionMapped(draft, session.id);
        refetchSessions();
        refetchDetail();
      },
    });
  };

  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);

  const [detailSession, setDetailSession] = useState<{
    session: EditionSession;
    index: number;
    tab: SessionDetailTab;
  } | null>(null);

  const [rescheduleSession, setRescheduleSession] = useState<{
    session: EditionSession;
    sessionNumber: number;
  } | null>(null);

  const [deleteSession, setDeleteSession] = useState<{
    session: EditionSession;
    sessionNumber: number;
  } | null>(null);

  const { deleteSession: triggerDeleteSession, isDeleting: isDeletingSession } =
    useDeleteSession(corporateId, editionId, kind);

  const handleOpenRescheduleSession = (
    session: EditionSession,
    sessionNumber: number,
  ) => {
    setDetailSession(null);
    setRescheduleSession({ session, sessionNumber });
  };

  const handleConfirmRescheduleSession = async (
    startIso: string,
    endIso: string,
  ) => {
    if (!rescheduleSession) return;
    await submitSession(
      {
        ...(rescheduleSession.session as any),
        start_date: startIso,
        end_date: endIso,
      },
      detail || {},
      sessions,
      rescheduleSession.session.id,
    );
    refetchSessions();
    refetchDetail();
    setRescheduleSession(null);
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteSession) return;
    try {
      await triggerDeleteSession(
        detail || {},
        sessions,
        deleteSession.session.id,
      );
      openToast({
        severity: "success",
        message: String(t("delete-session.success")),
      });
      refetchSessions();
      refetchDetail();
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-session.error")),
      });
    } finally {
      setDeleteSession(null);
    }
  };

  const [enrolledPage, setEnrolledPage] = useState(1);
  const [enrolledPageSize, setEnrolledPageSize] = useState(ENROLLED_PAGE_SIZE);
  const [enrolledSearch, setEnrolledSearch] = useState("");
  const [enrolledFieldFilters, setEnrolledFieldFilters] = useState<
    Record<string, any>
  >({});

  const {
    items: enrolledUsers,
    totalItems: enrolledTotal,
    totalPages: enrolledTotalPages,
    isLoading: enrolledLoading,
    mutate: refetchEnrolledUsers,
  } = useEditionEnrolledUsers({
    corporateId,
    editionId,
    page: enrolledPage,
    pageSize: enrolledPageSize,
    search: enrolledSearch || undefined,
    fieldFilters: enrolledFieldFilters,
  });

  const { can } = usePermissions();

  const { dataDetail: nonDeletableIds } = useFetchDataDetail(
    ServicesURL.getEditionsNotDeletable,
  );
  const isEditionNotDeletable = Array.isArray(nonDeletableIds)
    ? nonDeletableIds.includes(Number(editionId)) ||
      nonDeletableIds.includes(editionId)
    : false;

  const { deleteEdition, isDeleting: isDeletingEdition } =
    useDeleteEdition(kind);
  const [confirmDeleteEdition, setConfirmDeleteEdition] = useState(false);

  const { sendMail, isPreparing: isPreparingMail } = useEditionMailto({
    corporateId,
  });

  const handleSendEmail = async () => {
    const outcome = await sendMail({ editionId, kind });
    if (outcome === "URL_TOO_LONG") {
      openToast({
        severity: "warning",
        message: String(t("mail.url-too-long")),
      });
    } else if (outcome === "ERROR") {
      openToast({
        severity: "error",
        message: String(t("mail.error")),
      });
    }
  };

  const handleConfirmDeleteEdition = async () => {
    try {
      await deleteEdition({ editionId, corporateId });
      openToast({
        severity: "success",
        message: String(t("delete-edition.success")),
      });
      setConfirmDeleteEdition(false);
      navigate(-1);
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-edition.error")),
      });
      setConfirmDeleteEdition(false);
    }
  };

  const objectId = detail?.learning_objects?.id;

  const [rescheduleUser, setRescheduleUser] =
    useState<EditionEnrolledUser | null>(null);
  const [rescheduleEditions, setRescheduleEditions] = useState<
    EnrollableEdition[]
  >([]);
  const [isLoadingRescheduleEditions, setIsLoadingRescheduleEditions] =
    useState(false);
  const [deleteUser, setDeleteUser] = useState<EditionEnrolledUser | null>(
    null,
  );

  const {
    fetchEnrollableEditions,
    submitReschedule,
    awaitOutcome,
    isSubmitting: isReschedulingPatch,
  } = useRescheduleEnrollment();
  const { deleteEnrollment, isDeleting } = useDeleteEditionEnrollment();

  const handleOpenReschedule = async (u: EditionEnrolledUser) => {
    if (!objectId || !u.idUser) return;
    setRescheduleUser(u);
    setRescheduleEditions([]);
    setIsLoadingRescheduleEditions(true);
    try {
      const list = await fetchEnrollableEditions({
        corporateId,
        objectId,
        studentEid: u.idUser,
      });
      if (list.length === 0) {
        openToast({
          severity: "warning",
          message: String(t("reschedule.no-editions-available")),
        });
        setRescheduleUser(null);
        return;
      }
      setRescheduleEditions(list);
    } catch {
      openToast({
        severity: "error",
        message: String(t("reschedule.fetch-error")),
      });
      setRescheduleUser(null);
    } finally {
      setIsLoadingRescheduleEditions(false);
    }
  };

  const handleCloseReschedule = () => {
    if (isReschedulingPatch) return;
    setRescheduleUser(null);
    setRescheduleEditions([]);
  };

  const handleConfirmReschedule = async (newEditionId: number) => {
    if (!rescheduleUser?.idUser || !objectId) return;
    const userId = rescheduleUser.idUser;
    try {
      await submitReschedule({
        corporateId,
        objectId,
        studentEid: userId,
        newEditionId,
      });
    } catch {
      openToast({
        severity: "error",
        message: String(t("reschedule.error")),
      });
      setRescheduleUser(null);
      setRescheduleEditions([]);
      return;
    }

    setRescheduleUser(null);
    setRescheduleEditions([]);
    openToast({
      severity: "info",
      message: String(t("reschedule.submitted")),
    });

    const outcome = await awaitOutcome({
      corporateId,
      objectId,
      studentEid: userId,
      newEditionId,
    });

    if (outcome.status === "COMPLETE") {
      openToast({
        severity: "success",
        message: String(t("reschedule.success")),
      });
    } else if (outcome.status === "TIMEOUT") {
      openToast({
        severity: "error",
        message: String(t("reschedule.timeout")),
      });
    } else {
      openToast({
        severity: "error",
        message: outcome.errorMessage || String(t("reschedule.error")),
      });
    }
    refetchEnrolledUsers();
    refetchDetail();
  };

  const handleConfirmDelete = async () => {
    if (!deleteUser?.idUser || !objectId) return;
    try {
      await deleteEnrollment({
        corporateId,
        objectId,
        studentEid: deleteUser.idUser,
        editionId,
      });
      openToast({
        severity: "success",
        message: String(t("delete-enrollment.success")),
      });
      refetchEnrolledUsers();
      refetchDetail();
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-enrollment.error")),
      });
    } finally {
      setDeleteUser(null);
    }
  };

  const tabs = [
    { key: "sessions", label: String(t("tabs.sessions")) },
    ...(can(PERMISSIONS.registry.read)
      ? [{ key: "register", label: String(t("tabs.register")) }]
      : []),
    { key: "enrolled-users", label: String(t("tabs.enrolled-users")) },
  ];

  const totalSeats = detail?.total_places ?? detail?.max_capacity ?? 0;
  const availableSeats = Math.max(
    0,
    totalSeats - (detail?.occupied_places ?? 0),
  );

  const headerIconBtn = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    disabled?: boolean,
  ) => (
    <ButtonTertiaryIcon
      onClick={onClick}
      aria-label={label}
      disabled={disabled}
      startIcon={icon}
    />
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ pr: 3 }}>
        <Breadcrumb />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack flexDirection="column" gap={"1.5rem"}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <ButtonTertiaryIcon
                  onClick={() => navigate(-1)}
                  aria-label="back"
                  sx={{
                    paddingLeft: "0",
                  }}
                >
                  <ArrowLeft size={"1.25rem"} />
                </ButtonTertiaryIcon>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: theme.palette.exploreCardText,
                  }}
                >
                  {detail?.edition_name || `#${editionId}`}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <PermissionGate
                  permission={editionPerm(kind, "retrieve")}
                  mode="disable"
                >
                  {headerIconBtn(<Pencil size={18} />, "edit", () =>
                    navigate(
                      { pathname: "edit", search: `?kind=${kind}` },
                      { relative: "path" },
                    ),
                  )}
                </PermissionGate>
                <PermissionGate
                  permission={editionPerm(kind, "update")}
                  mode="disable"
                >
                  {headerIconBtn(
                    <Trash2 size={18} />,
                    "delete",
                    () => setConfirmDeleteEdition(true),
                    isEditionNotDeletable,
                  )}
                </PermissionGate>
              </Box>
            </Box>
            <Stack flexDirection="column" gap={"0.5rem"}>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: theme.palette.exploreCardText,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Calendar size="1.25rem" />{" "}
                {formatRange(detail?.start_date, detail?.end_date)}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: theme.palette.systemSecondary01,
                }}
              >
                <Users size={14} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                  {t("places", {
                    capacity: `${availableSeats}/${totalSeats}`,
                    interpolation: { escapeValue: false },
                  })}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <PermissionGate
                permission={editionPerm(kind, "enrollment")}
                mode="disable"
              >
                <ButtonPrimary
                  onClick={() =>
                    corporateId &&
                    openEditionEnrollmentModal({
                      editionId: Number(editionId),
                      objectId: Number(idLO),
                      corporateId,
                      objectTitle:
                        detail?.learning_objects?.label ||
                        detail?.edition_name ||
                        undefined,
                      onSuccess: () => {
                        refetchEnrolledUsers();
                        setActiveTab("enrolled-users");
                      },
                    })
                  }
                >
                  {t("actions.enroll")}
                </ButtonPrimary>
              </PermissionGate>
              <PermissionGate
                permission={PERMISSIONS.enrollment.read}
                mode="disable"
              >
                <ButtonSecondary
                  onClick={handleSendEmail}
                  disabled={isPreparingMail}
                >
                  {t("actions.send-email")}
                </ButtonSecondary>
              </PermissionGate>
            </Box>

            <TabsCst
              tabs={tabs}
              activeKey={activeTab}
              onChange={(k) => setActiveTab(k as DetailTab)}
            />

            {activeTab === "sessions" && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("tabs.sessions")}
                  </Typography>
                  <PermissionGate
                    permission={editionPerm(kind, "update")}
                    mode="disable"
                  >
                    <ButtonTertiary
                      endIcon={<Plus size={"1rem"} />}
                      onClick={handleAddSession}
                    >
                      {t("session.add")}
                    </ButtonTertiary>
                  </PermissionGate>
                </Box>

                {sessionsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : sessions.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                      color: theme.palette.text.primary,
                      fontSize: "13px",
                      borderRadius: "12px",
                      border: `1px dashed ${theme.palette.divider}`,
                    }}
                  >
                    {t("session.empty")}
                  </Box>
                ) : (
                  sessions.map((session, index) => {
                    const sessionNumber = index + 1;
                    const isEditionPublished =
                      !!detail?.publish_date &&
                      dayjs().isAfter(dayjs(detail.publish_date));
                    const menuActions: ListItemAction[] = [
                      {
                        label: String(t("session.actions.reschedule")),
                        icon: <CalendarClock size={16} />,
                        onClick: () =>
                          handleOpenRescheduleSession(session, sessionNumber),
                      },
                      {
                        label: String(t("session.actions.edit")),
                        icon: <Pencil size={16} />,
                        onClick: () => handleEditSession(session),
                      },
                      {
                        label: String(t("session.actions.delete")),
                        icon: <Trash2 size={16} />,
                        disabled: isEditionPublished,
                        onClick: () =>
                          setDeleteSession({ session, sessionNumber }),
                      },
                    ];
                    return (
                      <SessionCard
                        key={session.id}
                        session={session}
                        kind={kind}
                        onVerifyInvites={() =>
                          setDetailSession({
                            session,
                            index,
                            tab: "verify-invites",
                          })
                        }
                        onDetails={() =>
                          setDetailSession({ session, index, tab: "details" })
                        }
                        menuActions={menuActions}
                      />
                    );
                  })
                )}
              </Box>
            )}

            {activeTab === "register" && (
              <RegistryTab corporateId={corporateId} editionId={editionId} />
            )}

            {activeTab === "enrolled-users" && (
              <EnrollmentListShell
                search={enrolledSearch}
                onSearchSubmit={(v) => {
                  setEnrolledSearch(v);
                  setEnrolledPage(1);
                }}
                hideSearch
                filtersPageId={PagesID["enrollment.subscribed.edition"]}
                fieldFilters={enrolledFieldFilters}
                onApplyFilters={(next) => {
                  setEnrolledFieldFilters(next);
                  setEnrolledPage(1);
                }}
                filtersExcludeKeys={["editionId"]}
                isLoading={enrolledLoading}
                isEmpty={enrolledUsers.length === 0}
                emptyLabel={String(t("enrolled-users.empty"))}
                page={enrolledPage}
                pageSize={enrolledPageSize}
                totalItems={enrolledTotal}
                totalPages={enrolledTotalPages}
                onPageChange={setEnrolledPage}
                onPageSizeChange={(next) => {
                  setEnrolledPageSize(next);
                  setEnrolledPage(1);
                }}
              >
                {enrolledUsers.map((u) => (
                  <Box
                    key={String(getEditionUserId(u))}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {getEditionUserName(u)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {`ID: ${getEditionUserId(u)}${
                          u.email ? ` | ${u.email}` : ""
                        }`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenReschedule(u)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: "8px",
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("enrolled-users.reschedule")}
                      </Button>
                      <IconButton
                        onClick={() => setDeleteUser(u)}
                        aria-label="delete enrollment"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </EnrollmentListShell>
            )}
          </Stack>
        )}

        {detailSession && (
          <SessionDetailModal
            session={detailSession.session}
            kind={kind}
            corporateId={corporateId}
            editionId={editionId}
            sessionNumber={detailSession.index + 1}
            contextLabel={[
              detail?.learning_objects?.label,
              detail?.edition_name,
            ]
              .filter(Boolean)
              .join(" - ")}
            initialTab={detailSession.tab}
            onEditSession={() => handleEditSession(detailSession.session)}
            onOpenReschedule={() =>
              handleOpenRescheduleSession(
                detailSession.session,
                detailSession.index + 1,
              )
            }
            onDeleteSession={() => {
              const session = detailSession.session;
              const sessionNumber = detailSession.index + 1;
              setDetailSession(null);
              setDeleteSession({ session, sessionNumber });
            }}
            deleteDisabled={
              !!detail?.publish_date &&
              dayjs().isAfter(dayjs(detail.publish_date))
            }
            onClose={() => setDetailSession(null)}
          />
        )}

        {rescheduleSession && (
          <RescheduleSessionModal
            open
            session={rescheduleSession.session}
            sessionNumber={rescheduleSession.sessionNumber}
            contextLabel={editionContextLabel()}
            isSubmitting={isSessionSubmitting}
            onClose={() => setRescheduleSession(null)}
            onConfirm={handleConfirmRescheduleSession}
          />
        )}

        {deleteSession && (
          <ConfirmDialogCst
            open
            title={String(t("delete-session.title"))}
            description={String(
              t("delete-session.description", {
                number: deleteSession.sessionNumber,
              }),
            )}
            confirmLabel={String(t("delete-session.confirm"))}
            cancelLabel={String(t("delete-session.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeletingSession}
            submittingLabel={String(t("delete-session.deleting"))}
            onClose={() => setDeleteSession(null)}
            onConfirm={handleConfirmDeleteSession}
          />
        )}

        {durationWarn && (
          <ConfirmDialogCst
            open
            title={String(t("duration-warning.title"))}
            description={String(t("duration-warning.description"))}
            confirmLabel={String(t("duration-warning.confirm"))}
            cancelLabel={String(t("duration-warning.cancel"))}
            confirmVariant="primary"
            onClose={() => {
              const r = durationWarn.resolve;
              setDurationWarn(null);
              r(false);
            }}
            onConfirm={() => {
              const r = durationWarn.resolve;
              setDurationWarn(null);
              r(true);
            }}
          />
        )}

        {rescheduleUser && (
          <RescheduleEnrollmentModal
            open
            editions={rescheduleEditions}
            isLoadingEditions={isLoadingRescheduleEditions}
            isSubmitting={isReschedulingPatch}
            onClose={handleCloseReschedule}
            onConfirm={handleConfirmReschedule}
          />
        )}

        {deleteUser && (
          <ConfirmDialogCst
            open
            title={String(t("delete-enrollment.title"))}
            description={String(
              t("delete-enrollment.description-user", {
                user: getEditionUserName(deleteUser),
              }),
            )}
            confirmLabel={String(t("delete-enrollment.confirm"))}
            cancelLabel={String(t("delete-enrollment.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeleting}
            submittingLabel={String(t("delete-enrollment.deleting"))}
            onClose={() => setDeleteUser(null)}
            onConfirm={handleConfirmDelete}
          />
        )}

        {confirmDeleteEdition && (
          <ConfirmDialogCst
            open
            title={String(t("delete-edition.title"))}
            description={String(
              t("delete-edition.description", {
                name:
                  detail?.edition_name ||
                  detail?.edition_code ||
                  `#${editionId}`,
              }),
            )}
            confirmLabel={String(t("delete-edition.confirm"))}
            cancelLabel={String(t("delete-edition.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeletingEdition}
            submittingLabel={String(t("delete-edition.deleting"))}
            onClose={() => setConfirmDeleteEdition(false)}
            onConfirm={handleConfirmDeleteEdition}
          />
        )}
      </Box>
    </AnimationFadeIn>
  );
};

export default EditionDetailPage;
